import { useEffect, useRef, useState } from 'react'
import { Bold, Italic, List, Code, Link as LinkIcon, X } from 'lucide-react'

interface RichEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export const RichEditor = ({ value, onChange, placeholder }: RichEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [showSource, setShowSource] = useState(false)

  // Link feature states
  const [showLinkPrompt, setShowLinkPrompt] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkText, setLinkText] = useState('')
  const [linkTarget, setLinkTarget] = useState('_blank')
  const [savedSelection, setSavedSelection] = useState<Range | null>(null)

  useEffect(() => {
    if (!showSource && editorRef.current && value !== editorRef.current.innerHTML) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || ''
      }
    }
  }, [value, showSource])

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const execCmd = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
  }

  const saveSelection = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      setSavedSelection(selection.getRangeAt(0))
    }
  }

  const restoreSelection = () => {
    if (savedSelection) {
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(savedSelection)
    }
  }

  const handleLinkClick = () => {
    saveSelection()
    setShowLinkPrompt(true)

    // Si el usuario tiene texto seleccionado, usarlo como valor inicial para el Texto del enlace
    const selection = window.getSelection()
    const selectedText = selection && selection.toString().length > 0 ? selection.toString() : ''

    setLinkUrl('')
    setLinkText(selectedText)
    setLinkTarget('_blank')
  }

  const insertLink = () => {
    if (!linkUrl) {
      setShowLinkPrompt(false)
      return
    }
    restoreSelection()

    // Determinar qué texto mostrar: linkText, o si está vacío, linkUrl
    const textToShow = linkText.trim() ? linkText.trim() : linkUrl

    // Insert raw HTML for link to support target attribute 
    const linkHTML = `<a href="${linkUrl}" target="${linkTarget}" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline">${textToShow}</a>`
    document.execCommand('insertHTML', false, linkHTML)

    setShowLinkPrompt(false)
    editorRef.current?.focus()
    handleInput()
  }

  return (
    <div className={`border rounded-md bg-white dark:bg-slate-800 transition-colors relative ${isFocused ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300 dark:border-slate-600'}`}>
      <div className="flex items-center gap-1 p-2 border-b dark:border-slate-600 bg-gray-50 dark:bg-slate-700 rounded-t-md">
        {!showSource && (
          <>
            <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 transition-colors" title="Negrita">
              <Bold size={16} />
            </button>
            <button type="button" onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 transition-colors" title="Cursiva">
              <Italic size={16} />
            </button>
            <div className="w-px h-4 bg-gray-300 dark:bg-slate-500 mx-1" />
            <button type="button" onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 transition-colors" title="Lista con viñetas">
              <List size={16} />
            </button>
            <div className="w-px h-4 bg-gray-300 dark:bg-slate-500 mx-1" />
            <button type="button" onClick={handleLinkClick} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 transition-colors" title="Insertar Enlace">
              <LinkIcon size={16} />
            </button>
            <div className="flex-1" />
          </>
        )}
        {showSource && <div className="flex-1 text-xs text-gray-500 dark:text-gray-400 font-medium px-2">Editando código HTML</div>}

        <button
          type="button"
          onClick={() => setShowSource(!showSource)}
          className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors ${showSource ? 'bg-gray-200 dark:bg-slate-600 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'}`}
          title="Ver código fuente"
        >
          <Code size={16} />
        </button>
      </div>

      {showLinkPrompt && (
        <div className="absolute top-12 left-2 z-10 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 shadow-xl rounded-md p-3 w-64 text-sm flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700 dark:text-gray-200">Añadir Enlace</span>
            <button type="button" onClick={() => setShowLinkPrompt(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
              <X size={14} />
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">URL del enlace</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="w-full border rounded px-2 py-1.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Texto a mostrar</label>
            <input
              type="text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Ej: Ver video"
              className="w-full border rounded px-2 py-1.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Abrir en</label>
            <select
              value={linkTarget}
              onChange={(e) => setLinkTarget(e.target.value)}
              className="w-full border rounded px-2 py-1.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            >
              <option value="_blank">Nueva pestaña (_blank)</option>
              <option value="_self">Misma pestaña (_self)</option>
            </select>
          </div>
          <button
            type="button"
            onClick={insertLink}
            className="w-full bg-blue-600 text-white rounded py-1.5 font-medium hover:bg-blue-700 transition-colors"
          >
            Insertar
          </button>
        </div>
      )}

      {showSource ? (
        <textarea
          className="w-full p-3 min-h-[100px] outline-none font-mono text-sm text-gray-700 dark:text-gray-200 bg-transparent resize-y"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Código HTML..."
        />
      ) : (
        <div
          ref={editorRef}
          className="p-3 min-h-[100px] outline-none prose prose-sm max-w-none text-gray-700 dark:text-gray-200 dark:prose-invert [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5"
          contentEditable
          onInput={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      )}

      {/* Placeholder simulation */}
      {!value && !isFocused && !showSource && placeholder && (
        <div className="absolute pointer-events-none text-gray-400 dark:text-gray-500 p-3 top-[41px] left-0">
          {placeholder}
        </div>
      )}
    </div>
  )
}
